import React, { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';

import { useNotifications } from 'src/notifications';
import { isStandardErrorResponse } from 'src/api/common';
import { type IContact, useCreateContact } from 'src/services/api';
import {
    type IContactFieldsState,
    areContactFieldsValid,
    ContactFields,
    EMPTY_CONTACT_FIELDS,
} from 'src/components/edit-contacts-modal/contact-fields';

interface Props {
    /** Properties the new contact is linked to on creation. */
    propertyIds?: number[];
    onSave(contact: IContact): void;
    onCancel(): void;
}

export default function CreateContactModal({ propertyIds = [], onSave, onCancel }: Props) {
    const { addNotification } = useNotifications();
    const { mutateAsync: createContact, isLoading } = useCreateContact();

    const [fields, setFields] = useState<IContactFieldsState>(EMPTY_CONTACT_FIELDS);

    const handleSave = async () => {
        try {
            const contact = await createContact({
                email: fields.email.trim(),
                name: fields.name.trim() || null,
                phone_number: fields.phoneNumber.trim() || null,
                property_ids: propertyIds,
            });
            addNotification('Contact created', 'success');
            onSave(contact);
        } catch (error) {
            if (isStandardErrorResponse(error)) {
                addNotification(error.msg || 'Failed to create contact', 'error');
            } else {
                addNotification('An unknown error occurred', 'error');
            }
        }
    };

    return (
        <Dialog open onClose={onCancel} maxWidth="sm" fullWidth>
            <DialogTitle>Create New Contact</DialogTitle>

            <DialogContent>
                <ContactFields fields={fields} setFields={setFields} />
            </DialogContent>

            <DialogActions>
                <Button variant="outlined" onClick={onCancel} data-testid="create-contact-cancel-button">
                    Cancel
                </Button>
                <LoadingButton
                    variant="contained"
                    loading={isLoading}
                    disabled={!areContactFieldsValid(fields)}
                    onClick={handleSave}
                    data-testid="create-contact-save-button"
                >
                    Create Contact
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
}
